import { createFileRoute, useLocation } from "@tanstack/react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Building2,
  Copy,
  Check,
  ExternalLink,
  ChevronDown,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { canAdminWrite } from "@/lib/auth";
import { getMerchantQuery } from "@/lib/merchant-query";
import { useImpersonateMerchant } from "@/hooks/use-impersonate-merchant";
import { ConfirmationOfPayerToggle } from "./-components/confirmation-of-payer-toggle";
import { SwiftPayoutsToggle } from "./-components/swift-payouts-toggle";
import { ManageOptimusCustomer } from "./-components/manage-optimus-customer";
import { PayinAccounts } from "./-components/payin-accounts";
import { useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Application, Merchant } from "@/types/merchant";
import { cn } from "@/lib/utils";
import { FxSetupTimeline } from "./-components/fx-setup-timeline";

type MerchantTab = "payin" | "optimus" | "fx";
const MERCHANT_TABS: MerchantTab[] = ["payin", "optimus", "fx"];

export const Route = createFileRoute(
  "/(app)/_admin-layout/admin_/merchants_/$merchantId",
)({
  component: MerchantDetailComponent,
  loader: async ({ context, params, cause }) => {
    const query = getMerchantQuery({
      accessToken: context.accessToken,
      merchantId: params.merchantId,
    });
    // router.invalidate() after a save: wait for the fresh configuration.
    if (cause === "stay") {
      await context.queryClient.fetchQuery({ ...query, staleTime: 0 });
      return;
    }
    // Seen before (or preloaded on hover): open straight away and refresh
    // behind it rather than blocking the click on the network.
    if (context.queryClient.getQueryData(query.queryKey)) {
      if (cause === "enter") {
        void context.queryClient.prefetchQuery({ ...query, staleTime: 0 });
      }
      return;
    }
    await context.queryClient.fetchQuery(query);
  },
});

function CopyValue({
  value,
  className,
}: {
  value: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success("Copied");
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      onClick={copy}
      className={cn(
        "inline-flex items-center gap-1.5 font-mono bg-slate-100 hover:bg-slate-200 px-2 py-0.5 rounded transition-colors",
        className || "text-sm",
      )}
    >
      {value}
      {copied ? (
        <Check className="h-3 w-3 text-green-600" />
      ) : (
        <Copy className="h-3 w-3 text-slate-400" />
      )}
    </button>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[11px] font-bold text-slate-900 mb-3 flex items-center gap-2 uppercase tracking-widest border-b border-slate-200 pb-2">
      {children}
    </h3>
  );
}

function ApplicationSection({
  application,
  merchantId,
  accessToken,
  merchant,
  defaultOpen,
  activeTab,
  onTabChange,
}: {
  application: Application;
  merchantId: string;
  accessToken: string;
  defaultOpen: boolean;
  merchant: Merchant;
  activeTab: MerchantTab;
  onTabChange: (tab: MerchantTab) => void;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const { authorities } = Route.useRouteContext();
  const canWrite = canAdminWrite(authorities);
  const payinCount = application.payeeDetailsPerCurrency?.length || 0;

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="border rounded-2xl border-slate-200 bg-white overflow-hidden shadow-sm shadow-slate-200/70"
    >
      <CollapsibleTrigger className="w-full text-left">
        <div className="flex items-center justify-between p-4 bg-[#F8F9FF] hover:bg-[#F0F2FF] transition-colors border-b">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center shrink-0">
              <Building2 className="h-5 w-5 text-volume-text-purple" />
            </div>
            <div>
              <div className="font-semibold text-slate-900">
                {application.applicationName}
              </div>
              <div className="mt-0.5">
                <CopyValue
                  value={application.applicationId}
                  className="text-[10px] text-slate-500 bg-white border border-slate-200"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2">
              {application.hasVirtualAccountsEnabled && (
                <Badge
                  variant="outline"
                  className="bg-green-50 text-green-700 border-green-200"
                >
                  VA
                </Badge>
              )}
              {application.confirmationOfPayerEnabled && (
                <Badge
                  variant="outline"
                  className="bg-volume-light-purple/40 text-volume-text-purple border-volume-text-purple/20"
                >
                  CoP
                </Badge>
              )}
              <Badge
                variant="secondary"
                className="bg-white text-slate-600 border-slate-200 font-medium"
              >
                {payinCount} account{payinCount !== 1 ? "s" : ""}
              </Badge>
            </div>
            <ChevronDown
              className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
            />
          </div>
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="p-6">
          <Tabs
            value={activeTab}
            onValueChange={(value) => onTabChange(value as MerchantTab)}
            className="space-y-4"
          >
            <TabsList className="bg-transparent border-b border-slate-200 rounded-none p-0 h-auto">
              <TabsTrigger
                value="payin"
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-volume-text-purple data-[state=active]:text-volume-text-purple rounded-none border-b-2 border-transparent px-4 py-2 font-medium"
              >
                Payin Accounts
              </TabsTrigger>
              <TabsTrigger
                value="optimus"
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-green-600 data-[state=active]:text-green-600 rounded-none border-b-2 border-transparent px-4 py-2 font-medium"
              >
                Optimus VA
              </TabsTrigger>
              <TabsTrigger
                value="fx"
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-purple-600 data-[state=active]:text-purple-600 rounded-none border-b-2 border-transparent px-4 py-2 font-medium"
              >
                FX
              </TabsTrigger>
            </TabsList>

            {!canWrite && (
              <p className="text-sm text-muted-foreground">
                Read-only access. Configuration changes require a write admin.
              </p>
            )}
            <fieldset disabled={!canWrite} className="min-w-0">
              <TabsContent value="payin" className="space-y-6 mt-3">
                {/* Quick Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-1">
                      Virtual Accounts
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        className={`h-2 w-2 rounded-full ${application.hasVirtualAccountsEnabled ? "bg-green-600" : "bg-slate-400"}`}
                      />
                      <span className="text-sm font-bold text-slate-900">
                        {application.hasVirtualAccountsEnabled
                          ? "Active"
                          : "Disabled"}
                      </span>
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600">
                        CoP Status
                      </div>
                      <ConfirmationOfPayerToggle application={application} />
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        className={`h-2 w-2 rounded-full ${application.confirmationOfPayerEnabled ? "bg-volume-text-purple" : "bg-slate-400"}`}
                      />
                      <span className="text-sm font-bold text-slate-900">
                        {application.confirmationOfPayerEnabled
                          ? "Active"
                          : "Disabled"}
                      </span>
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600">
                        SWIFT Payouts
                      </div>
                      <SwiftPayoutsToggle application={application} />
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        className={`h-2 w-2 rounded-full ${application.swiftPayoutsEnabled ? "bg-purple-600" : "bg-slate-400"}`}
                      />
                      <span className="text-sm font-bold text-slate-900">
                        {application.swiftPayoutsEnabled
                          ? "Enabled"
                          : "Disabled"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <SectionHeading>
                    <div className="h-1 w-1 rounded-full bg-volume-text-purple" />
                    Payin Accounts
                    <span className="ml-2 text-[10px] font-normal text-slate-500 tracking-normal normal-case">
                      ({payinCount})
                    </span>
                  </SectionHeading>
                  <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm shadow-slate-200/70">
                    <PayinAccounts application={application} />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="optimus" className="space-y-4 mt-3">
                <SectionHeading>
                  <div className="h-1 w-1 rounded-full bg-green-600" />
                  Optimus Virtual Accounts
                </SectionHeading>
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm shadow-slate-200/70">
                  <ManageOptimusCustomer
                    application={application}
                    merchantId={merchantId}
                  />
                </div>
              </TabsContent>

              <TabsContent value="fx" className="space-y-4 mt-3">
                <FxSetupTimeline
                  applicationId={application.applicationId}
                  merchantId={merchantId}
                  accessToken={accessToken}
                  merchant={merchant}
                />
              </TabsContent>
            </fieldset>
          </Tabs>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function MerchantDetailComponent() {
  const context = Route.useRouteContext();
  const { merchantId } = Route.useParams();
  const { merchant } = useSuspenseQuery(
    getMerchantQuery({ accessToken: context.accessToken, merchantId }),
  ).data;
  const navigate = Route.useNavigate();
  const location = useLocation();
  const impersonate = useImpersonateMerchant(context.accessToken);

  const pathSegments = location.pathname.split("/").filter(Boolean);
  const tabCandidate = pathSegments[pathSegments.length - 1];
  const activeTab: MerchantTab = MERCHANT_TABS.includes(
    tabCandidate as MerchantTab,
  )
    ? (tabCandidate as MerchantTab)
    : "payin";

  const navigateToTab = async (tab: MerchantTab) => {
    await navigate({
      to: "/admin/merchants/$merchantId/$tab",
      params: { merchantId: merchant.merchantId, tab },
    });
  };

  return (
    <div className="space-y-6 pt-2 pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate({ to: "/admin" })}
            className="mb-1 -ml-2 text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Merchants
          </Button>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            {merchant.companyName}
          </h1>
          <div className="flex flex-wrap items-center gap-2 pt-1 text-xs text-slate-500">
            <CopyValue value={merchant.merchantId} className="text-xs" />
            {merchant.email ? (
              <CopyValue value={merchant.email} className="text-xs" />
            ) : (
              <span className="italic">No email set</span>
            )}
          </div>
        </div>

        <Button
          onClick={() => impersonate.mutate(merchant.merchantId)}
          disabled={impersonate.isPending}
          aria-busy={impersonate.isPending}
          className="bg-volume-text-purple hover:bg-volume-text-purple/90 text-white shadow-sm shrink-0"
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          {impersonate.isPending ? "Opening merchant…" : "Impersonate Merchant"}
        </Button>
      </div>

      <div className="space-y-4">
        <h2 className="text-lg font-bold text-slate-900">
          Applications
          <span className="ml-2 text-sm font-semibold text-slate-600">
            ({merchant.applications.length})
          </span>
        </h2>

        {merchant.applications.length > 0 ? (
          <div className="space-y-4">
            {merchant.applications.map((app, index) => (
              <ApplicationSection
                key={app.applicationId}
                application={app}
                merchantId={merchant.merchantId}
                accessToken={context.accessToken}
                defaultOpen={index === 0}
                merchant={merchant}
                activeTab={activeTab}
                onTabChange={(tab) => void navigateToTab(tab)}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center">
            <Building2 className="h-8 w-8 text-slate-300 mx-auto mb-3" />
            <h3 className="text-sm font-semibold text-slate-900">
              No applications
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              This merchant doesn't have any applications yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
